/**
 * Assembling graph input: the Dexie search mirror joined to the personal
 * workspace snapshot.
 *
 * PERSONAL SPACE ONLY (invariant 9 + 15). The search index deliberately covers
 * only the personal workspace, because shared folders are online-only and
 * nothing of theirs may persist locally. Shared spaces therefore get an empty
 * state, exactly like the existing backlinks panel.
 */
import type { WorkspaceItem } from "@hydra-notes/shared";
import { getData } from "@/data/store";
import { searchService } from "@/data/search/client";
import type { GraphCorpus, GraphInput } from "./types";

/** openByTitle's normalization, verbatim. */
export function normalizeTitle(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Full path by walking parentId.
 *
 * The hop guard is not paranoia: parentId lives in a CRDT and a concurrent
 * move can transiently produce a cycle. revealInTree uses the same defense.
 */
export function buildPaths(items: Map<string, WorkspaceItem>): Map<string, string> {
  const memo = new Map<string, string>();
  const seg = (id: string, hops: number): string => {
    const cached = memo.get(id);
    if (cached !== undefined) return cached;
    const it = items.get(id);
    if (!it || hops > 64) return "";
    const own = it.title.trim() || "Untitled";
    const parent = it.parentId ? seg(it.parentId, hops + 1) : "";
    const full = parent ? `${parent}/${own}` : own;
    memo.set(id, full);
    return full;
  };
  const out = new Map<string, string>();
  for (const id of items.keys()) out.set(id, seg(id, 0));
  return out;
}

/**
 * Build the title/alias lookup the same way openByTitle resolves a wikilink:
 * walk the snapshot IN ORDER, skip trashed, treat title and aliases as equal
 * weight, and let the FIRST match win. Matching that .find() semantics exactly
 * is what guarantees the graph can never disagree with click-through.
 */
export function buildTitleIndex(snapshot: WorkspaceItem[]): Pick<GraphInput, "noteByTitle" | "offGraphTitles" | "collisions"> {
  const noteByTitle = new Map<string, string>();
  const offGraphTitles = new Set<string>();
  let collisions = 0;

  for (const item of snapshot) {
    if (item.trashedAt) continue;
    const keys = [item.title, ...(item.aliases ?? [])];
    const graphable = item.type === "note" && !item.archivedAt;
    for (const raw of keys) {
      const k = normalizeTitle(raw ?? "");
      if (!k) continue;
      if (graphable) {
        if (noteByTitle.has(k)) collisions++;
        else noteByTitle.set(k, item.id);
      } else {
        // a card, folder, task list, or archived note owns this title: a
        // wikilink to it still navigates, it is just not a graph node
        offGraphTitles.add(k);
      }
    }
  }
  return { noteByTitle, offGraphTitles, collisions };
}

export interface LoadedGraphInput {
  input: GraphInput;
  corpus?: GraphCorpus;
}

/**
 * Read everything the graph needs. `withText` is set only when a live query
 * has bare terms, since those are the only thing that needs note bodies.
 */
export async function loadGraphInput(withText: boolean): Promise<LoadedGraphInput> {
  const ctx = getData();
  const snapshot = ctx.workspace.getSnapshot();
  const items = new Map<string, WorkspaceItem>(snapshot.map((i) => [i.id, i]));
  const { noteByTitle, offGraphTitles, collisions } = buildTitleIndex(snapshot);
  const paths = buildPaths(items);

  if (withText) {
    const { rows, corpus } = await searchService.graphRowsWithText();
    return { input: { rows, items, noteByTitle, offGraphTitles, paths, collisions }, corpus };
  }
  const rows = await searchService.graphRows();
  return { input: { rows, items, noteByTitle, offGraphTitles, paths, collisions } };
}
